<template>
  <v-app v-if="webSocketError" class="error">
    <div class="min-height100 d-flex flex-column justify-center center">
      <h1 class="h3n mb4">
        {{ webSocketError }}
      </h1>
      <div class="relative center pa-2">
        <v-btn
          color="third"
          style="margin-top: 16px; background-color:#c71900; color: #fff"
          @click="refreshPage"
        >
          Reconnecter
        </v-btn>
      </div>
    </div>
  </v-app>
  <v-app v-else-if="!isDisplayerRoute" class="light-grey-bg">
    <Sidebar />

    <AppBar />
    <v-main v-if="!loading" style="height:100vh;" :class="[{ 'overflow-y-auto': isFFEroute }]">
      <router-view />
      <Notification />
    </v-main>
    <div v-else class="d-flex justify-center align-center full-height">
      <Loader />
    </div>
  </v-app>
  <router-view v-else />
</template>

<script>
import { onBeforeMount, computed, ref, provide } from '@vue/composition-api';
import { set, format } from 'date-fns';
import { fr } from 'date-fns/esm/locale';
import Notification from '@/components/common/Notification.vue';

import { handleSessions } from '@/helpers/amplify';

import Sidebar from '@/components/template/SideBar.vue';
import AppBar from '@/components/template/AppBar.vue';
import Loader from '@/components/common/Loader.vue';
import store from '@/store';
import router from '@/router';

import { getUser, getToken, getLoginUrl } from '@/helpers/auth';
import { changeWorkspace } from '@/helpers/utils';
import { pegaseWorkspaces } from '@/helpers/request';

export default {
  components: {
    Notification,
    Sidebar,
    AppBar,
    Loader
  },
  setup (_, context) {
    const loading = computed(() => store.state.loading);
    const isDisplayerRoute = computed(() => context.root.$route.name === 'Display');

    const isFFEroute = computed(() => context.root.$route.name === 'FFeFeed');

    const isInstructorMobile = computed(() => store.getters.isIntructorOnMobile);
    const instructorSessions = computed(() => store.state.instructorSessions);

    const currentMonth = ref(format(new Date(), 'LLLL', { locale: fr }));
    const currentPickerDate = ref(format(new Date(), 'yyyy-MM-dd'));
    const currentWorkspace = computed(() => store.state.workspace);
    const workspaces = computed(() => store.getters.allowedWorkspaces);

    const style = computed(() => (isInstructorMobile.value ? 'height:100vh;' : ''));

    const addParticipantDialog = ref(false);
    const selectedSession = ref(null);

    const webSocketError = computed(() => store.state.webSocketError);

    const changeSelectedSession = value => {
      selectedSession.value = value;
    };

    const token = getToken();

    provide('addParticipantDialog', addParticipantDialog);
    provide('currentMonth', currentMonth);
    provide('currentPickerDate', currentPickerDate);
    provide('selectedSessionProduct', { selectedSession, changeSelectedSession });

    const initializeCommonResources = async query => {
      let sessionFetch = false;

      store.commit('setLoading', true);
      const siteWorkspaces = await pegaseWorkspaces();

      if (siteWorkspaces && siteWorkspaces.data) {
        store.commit('setWorkspaces', siteWorkspaces.data.map(w => ({
          code: w.workspace,
          name: w.name
        })));
      }

      const user = await getUser(token, window.location.origin);
      store.commit('setUser', { ...user, wtype: 'collaborator' });

      if (!store.getters.allowedWorkspaces.length) {
        window.localStorage.clear();
        window.location.href = getLoginUrl(window.location.origin, process.env.VUE_APP_SSO_CLIENT_ID);
        return false;
      }

      if (!store.getters.allowedWorkspaces.some(work => work.code === store.state.workspace.code)) {
        store.commit('setWorkspace', store.getters.allowedWorkspaces[0]);
      }

      await store.dispatch('getSiteInfo');

      if (query && query.startTime && query.id) {
        const date = format(parseInt(query.startTime * 1000), 'yyyy-MM-dd');
        // const oldDate = store.getters.selectedDate;
        store.commit('setSelectedDate', date);

        await handleSessions();
        sessionFetch = true;

        selectedSession.value = store.state.sessions.find(s => s.id === query.id);
        const url = router.app.$route;
        router.app.$router.replace({ ...url, query: {} });
      }

      if (!sessionFetch) {
        await handleSessions();
      }

      await store.dispatch('getReferences');

      await store.dispatch('allMonthlySessions', 'horses');

      store.commit('setLoading', false);

      const instructorWorkspaces = user.resources.filter(resource => resource.type === 'monitor');
      const isWorkspaceAllowed = instructorWorkspaces.some(w => workspaces.value.some(work => work.code === w.workspace));

      if (isInstructorMobile.value && !isWorkspaceAllowed) {
        for (let i = 0; i <= instructorWorkspaces.length; i += 1) {
          const work = workspaces.value.find(w => w.code === instructorWorkspaces[i].workspace);
          if (work && work.code !== currentWorkspace.value.code) {
            await changeWorkspace(work);
            break;
          }
        }
      }
      return true;
    };

    const instructorMobileRoute = async () => {
      await store.dispatch('instructorSessions');
      if (context.root.$route.name === 'SessionDetail') {
        const sessionId = context.root.$route.params.session_id;
        const session = instructorSessions.value.find(s => s.id === sessionId);
        store.commit('setSelectedSession', session);
        return false;
      }
      document.location.href = `#month_${format(set(new Date(), { date: 1 }), 'yyyy-MM-dd')}`;
      return true;
    };

    const customerDetailRoute = async query => {
      if (query.from === 'crm') {
        store.commit('setCurrentTab', 'customer');
        router.replace({ query: null });
      }
    };

    // hooks
    onBeforeMount(async () => {
      const { query } = context.root.$route;

      if (!isDisplayerRoute.value) {
        const res = await initializeCommonResources(query);
        if (!res) return;
      }

      if (isInstructorMobile.value) {
        const res = await instructorMobileRoute();
        if (!res) return;
      }

      if (query.hasOwnProperty('from')) {
        await customerDetailRoute(query);
      }

      store.commit('setLoading', false);
    });

    const refreshPage = () => {
      window.location.reload();
    };

    return { refreshPage, loading, isDisplayerRoute, isInstructorMobile, style, isFFEroute, selectedSession, webSocketError };
  }
};
</script>

<style>
   @import './assets/css/common.css';
</style>
